import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"
import { IResponse } from "constants/interfaces"

import { SportsbookSport } from "services/seo/type"

import useApi, { parseError } from "util/api"

import {
  BrandSiteConfig,
  BrandSiteConfigResponse,
  CmsDuplicateInput,
  CmsMediaItem,
  CmsMediaListResponse,
  CmsPage,
  CmsPageInput,
  CmsPageListQuery,
  CmsPageListResponse,
  CmsPageVersion,
  CmsRedirect,
  CmsRedirectImportResult,
  CmsRedirectInput,
  CmsRobots,
  CmsSeoRule,
  CmsSeoRuleInput,
  CmsSeoRuleTestResult,
  CmsSitemapSummary,
  CmsSlugCheck
} from "./type"

const EMPTY_PAGE_LIST: CmsPageListResponse = { items: [], page: 1, pageSize: 10, total: 0, total_pages: 0 }

/** Zero when undecodable: an unknown size must not block the upload. */
const readImageSize = (file: File): Promise<{ width: number; height: number }> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: image.naturalWidth, height: image.naturalHeight })
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      resolve({ width: 0, height: 0 })
    }
    image.src = url
  })

class CmsSeoService {
  public async listPages(query: CmsPageListQuery): Promise<CmsPageListResponse> {
    try {
      const { data } = await useApi.get<IResponse<CmsPageListResponse>>(REQ.ADMIN_DASHBOARD.CMS_SEO.PAGES, { params: query })
      return data?.response ?? EMPTY_PAGE_LIST
    } catch {
      toast.error("Failed to load CMS pages")
      return EMPTY_PAGE_LIST
    }
  }

  public async getPage(id: string): Promise<CmsPage | null> {
    try {
      const { data } = await useApi.get<IResponse<CmsPage>>(REQ.ADMIN_DASHBOARD.CMS_SEO.PAGE_BY_ID(id))
      return data?.response ?? null
    } catch {
      toast.error("Failed to load the page")
      return null
    }
  }

  public async createPage(input: Partial<CmsPageInput>): Promise<CmsPage | null> {
    try {
      const { data } = await useApi.post<IResponse<CmsPage>>(REQ.ADMIN_DASHBOARD.CMS_SEO.PAGES, input)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to create the page")
      return null
    }
  }

  public async updatePage(id: string, patch: Partial<CmsPageInput>): Promise<CmsPage | null> {
    try {
      const { data } = await useApi.patch<IResponse<CmsPage>>(REQ.ADMIN_DASHBOARD.CMS_SEO.PAGE_BY_ID(id), patch)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to save the page")
      return null
    }
  }

  public async deletePage(id: string): Promise<boolean> {
    try {
      await useApi.delete(REQ.ADMIN_DASHBOARD.CMS_SEO.PAGE_BY_ID(id))
      return true
    } catch (err: any) {
      parseError(err, "Failed to delete the page")
      return false
    }
  }

  /** The caller decides whether a rename also creates a 301 from the old URL. */
  public async publishPage(id: string, options: { publish_at?: string | null; create_redirect?: boolean }): Promise<CmsPage | null> {
    try {
      const { data } = await useApi.post<IResponse<CmsPage>>(REQ.ADMIN_DASHBOARD.CMS_SEO.PAGE_PUBLISH(id), options)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to publish the page")
      return null
    }
  }

  public async duplicatePage(id: string, input: CmsDuplicateInput): Promise<CmsPage | null> {
    try {
      const { data } = await useApi.post<IResponse<CmsPage>>(REQ.ADMIN_DASHBOARD.CMS_SEO.PAGE_DUPLICATE(id), input)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to duplicate the page")
      return null
    }
  }

  public async listVersions(id: string): Promise<CmsPageVersion[]> {
    try {
      const { data } = await useApi.get<IResponse<{ items: CmsPageVersion[] }>>(REQ.ADMIN_DASHBOARD.CMS_SEO.PAGE_VERSIONS(id))
      return data?.response?.items ?? []
    } catch {
      toast.error("Failed to load the page history")
      return []
    }
  }

  public async restoreVersion(id: string, version: number): Promise<CmsPage | null> {
    try {
      const { data } = await useApi.post<IResponse<CmsPage>>(REQ.ADMIN_DASHBOARD.CMS_SEO.PAGE_VERSION_RESTORE(id, version))
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to restore that version")
      return null
    }
  }

  public async previewUrl(id: string): Promise<string | null> {
    try {
      const { data } = await useApi.post<IResponse<{ url: string }>>(REQ.ADMIN_DASHBOARD.CMS_SEO.PAGE_PREVIEW_TOKEN(id))
      return data?.response?.url ?? null
    } catch (err: any) {
      parseError(err, "Failed to open a preview")
      return null
    }
  }

  public async validateSlug(input: { id?: string; locale: string; slug: string; parent_id?: string | null }): Promise<CmsSlugCheck | null> {
    try {
      const { data } = await useApi.post<IResponse<CmsSlugCheck>>(REQ.ADMIN_DASHBOARD.CMS_SEO.VALIDATE_SLUG, input)
      return data?.response ?? null
    } catch {
      return null
    }
  }

  /** The provider's sports, so the panel can list them rather than have an editor guess a path. */
  public async listSportsbookSports(): Promise<SportsbookSport[]> {
    try {
      const { data } = await useApi.get<IResponse<{ items: SportsbookSport[] }>>(REQ.ADMIN_DASHBOARD.CMS_SEO.SPORTSBOOK_SPORTS)
      return data?.response?.items ?? []
    } catch {
      toast.error("Failed to load the sports list")
      return []
    }
  }

  public async listSeoRules(): Promise<CmsSeoRule[]> {
    try {
      const { data } = await useApi.get<IResponse<{ items: CmsSeoRule[] }>>(REQ.ADMIN_DASHBOARD.CMS_SEO.SEO_RULES)
      return data?.response?.items ?? []
    } catch {
      toast.error("Failed to load SEO rules")
      return []
    }
  }

  public async createSeoRule(input: CmsSeoRuleInput): Promise<CmsSeoRule | null> {
    try {
      const { data } = await useApi.post<IResponse<CmsSeoRule>>(REQ.ADMIN_DASHBOARD.CMS_SEO.SEO_RULES, input)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to create the rule")
      return null
    }
  }

  public async updateSeoRule(id: string, patch: Partial<CmsSeoRuleInput>): Promise<CmsSeoRule | null> {
    try {
      const { data } = await useApi.patch<IResponse<CmsSeoRule>>(REQ.ADMIN_DASHBOARD.CMS_SEO.SEO_RULE_BY_ID(id), patch)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to save the rule")
      return null
    }
  }

  public async deleteSeoRule(id: string): Promise<boolean> {
    try {
      await useApi.delete(REQ.ADMIN_DASHBOARD.CMS_SEO.SEO_RULE_BY_ID(id))
      return true
    } catch (err: any) {
      parseError(err, "Failed to delete the rule")
      return false
    }
  }

  public async testSeoRules(url: string): Promise<CmsSeoRuleTestResult | null> {
    try {
      const { data } = await useApi.post<IResponse<CmsSeoRuleTestResult>>(REQ.ADMIN_DASHBOARD.CMS_SEO.SEO_RULE_TEST, { url })
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to test that URL")
      return null
    }
  }

  public async listRedirects(): Promise<CmsRedirect[]> {
    try {
      const { data } = await useApi.get<IResponse<{ items: CmsRedirect[] }>>(REQ.ADMIN_DASHBOARD.CMS_SEO.REDIRECTS)
      return data?.response?.items ?? []
    } catch {
      toast.error("Failed to load redirects")
      return []
    }
  }

  public async createRedirect(input: CmsRedirectInput): Promise<CmsRedirect | null> {
    try {
      const { data } = await useApi.post<IResponse<CmsRedirect>>(REQ.ADMIN_DASHBOARD.CMS_SEO.REDIRECTS, input)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to create the redirect")
      return null
    }
  }

  public async updateRedirect(id: string, patch: Partial<CmsRedirectInput>): Promise<CmsRedirect | null> {
    try {
      const { data } = await useApi.patch<IResponse<CmsRedirect>>(REQ.ADMIN_DASHBOARD.CMS_SEO.REDIRECT_BY_ID(id), patch)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to save the redirect")
      return null
    }
  }

  public async deleteRedirect(id: string): Promise<boolean> {
    try {
      await useApi.delete(REQ.ADMIN_DASHBOARD.CMS_SEO.REDIRECT_BY_ID(id))
      return true
    } catch (err: any) {
      parseError(err, "Failed to delete the redirect")
      return false
    }
  }

  public async importRedirects(file: File): Promise<CmsRedirectImportResult | null> {
    try {
      const body = new FormData()
      body.append("file", file)
      const { data } = await useApi.post<IResponse<CmsRedirectImportResult>>(REQ.ADMIN_DASHBOARD.CMS_SEO.REDIRECTS_IMPORT, body, {
        headers: { "Content-Type": "multipart/form-data" }
      })
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to import the CSV")
      return null
    }
  }

  public async listMedia(query: { page: number; pageSize: number; q?: string }): Promise<CmsMediaListResponse> {
    try {
      const { data } = await useApi.get<IResponse<CmsMediaListResponse>>(REQ.ADMIN_DASHBOARD.CMS_SEO.MEDIA, { params: query })
      return data?.response ?? { items: [], page: 1, pageSize: query.pageSize, total: 0, total_pages: 0 }
    } catch {
      toast.error("Failed to load the media library")
      return { items: [], page: 1, pageSize: query.pageSize, total: 0, total_pages: 0 }
    }
  }

  /** Dimensions come from the browser: core-api has no image decoder. */
  public async uploadMedia(file: File, alt: string): Promise<CmsMediaItem | null> {
    try {
      const { width, height } = await readImageSize(file)
      const body = new FormData()
      body.append("file", file)
      body.append("alt", alt)
      body.append("width", String(width))
      body.append("height", String(height))
      const { data } = await useApi.post<IResponse<CmsMediaItem>>(REQ.ADMIN_DASHBOARD.CMS_SEO.MEDIA, body, {
        headers: { "Content-Type": "multipart/form-data" }
      })
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to upload the file")
      return null
    }
  }

  public async updateMedia(id: string, patch: { alt: string }): Promise<CmsMediaItem | null> {
    try {
      const { data } = await useApi.patch<IResponse<CmsMediaItem>>(REQ.ADMIN_DASHBOARD.CMS_SEO.MEDIA_BY_ID(id), patch)
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to save the alt text")
      return null
    }
  }

  public async deleteMedia(id: string): Promise<boolean> {
    try {
      await useApi.delete(REQ.ADMIN_DASHBOARD.CMS_SEO.MEDIA_BY_ID(id))
      return true
    } catch (err: any) {
      parseError(err, "Failed to delete the file")
      return false
    }
  }

  public async getSiteUrl(): Promise<BrandSiteConfig | null> {
    try {
      const { data } = await useApi.get<IResponse<BrandSiteConfigResponse>>(REQ.ADMIN_DASHBOARD.CMS_SEO.SITE_URL)
      const response = data?.response
      if (!response) return null

      return {
        url: response.url ?? null,
        localePrefix: response.locale_prefix === "as-needed" ? "as-needed" : "always",
        defaultLocale: response.default_locale || "en"
      }
    } catch {
      // Degrades previews to relative paths rather than blocking the module.
      return null
    }
  }

  public async getSitemap(): Promise<CmsSitemapSummary | null> {
    try {
      const { data } = await useApi.get<IResponse<CmsSitemapSummary>>(REQ.ADMIN_DASHBOARD.CMS_SEO.SITEMAP)
      return data?.response ?? null
    } catch {
      toast.error("Failed to load the sitemap summary")
      return null
    }
  }

  public async getRobots(): Promise<CmsRobots | null> {
    try {
      const { data } = await useApi.get<IResponse<CmsRobots>>(REQ.ADMIN_DASHBOARD.CMS_SEO.ROBOTS)
      return data?.response ?? null
    } catch {
      toast.error("Failed to load robots.txt")
      return null
    }
  }

  public async saveRobots(body: string): Promise<CmsRobots | null> {
    try {
      const { data } = await useApi.patch<IResponse<CmsRobots>>(REQ.ADMIN_DASHBOARD.CMS_SEO.ROBOTS, { body })
      return data?.response ?? null
    } catch (err: any) {
      parseError(err, "Failed to save robots.txt")
      return null
    }
  }
}

export const cmsSeoService = new CmsSeoService()
